/**
 * Monitor - Live Call Monitoring
 * Socket.IO real-time + app theme + detail modal
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import {
  Phone, PhoneOff, Loader2, X, AlertTriangle,
  RefreshCw, Wifi, WifiOff, Clock, Bot, User,
  ArrowRight, Eye, Activity
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const SOCKET_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

// Normalize speaker value from any provider format
const normalizeSpeaker = (speaker) => {
  if (!speaker) return 'agent';
  switch (speaker.toLowerCase()) {
    case 'user':
    case 'caller':
    case 'human':
    case 'customer':
      return 'user';
    default:  // 'assistant', 'agent', 'bot', 'ai', etc.
      return 'agent';
  }
};

// ── Live timer ────────────────────────────────────────────────────────────────
function CallTimer({ startedAt, className = '' }) {
  const [display, setDisplay] = useState('00:00');
  useEffect(() => {
    const tick = () => {
      const secs = Math.max(0, Math.floor((Date.now() - new Date(startedAt)) / 1000));
      const m = String(Math.floor(secs / 60)).padStart(2, '0');
      const s = String(secs % 60).padStart(2, '0');
      setDisplay(`${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return <span className={`font-mono font-semibold ${className}`}>{display}</span>;
}

// ── Transcript message row ────────────────────────────────────────────────────
function TranscriptRow({ speaker, text, compact = false }) {
  const isUser = normalizeSpeaker(speaker) === 'user';
  return (
    <div className={`flex gap-2 ${compact ? 'items-start' : 'items-start'}`}>
      <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
        isUser ? 'bg-blue-100' : 'bg-primary-100'
      }`}>
        {isUser
          ? <User className="w-3 h-3 text-blue-600" />
          : <Bot className="w-3 h-3 text-primary-600" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-xs font-semibold ${isUser ? 'text-blue-600' : 'text-primary-600'}`}>
          {isUser ? 'Caller' : 'Agent'}
        </span>
        <p className={`text-gray-700 ${compact ? 'text-xs' : 'text-sm'} leading-relaxed`}>
          {text}
        </p>
      </div>
    </div>
  );
}

// ── Call card ─────────────────────────────────────────────────────────────────
function CallCard({ call, transcript, onHangup, isHangingUp, onClick, canHangup }) {
  const lastLines = transcript.slice(-4);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-primary-200 transition-all cursor-pointer flex flex-col"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                {call.caller_number || 'Unknown Caller'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {call.agent_name || 'Agent'} · {call.provider || ''}
              </p>
            </div>
          </div>
          <CallTimer startedAt={call.started_at} className="text-green-600 text-sm shrink-0" />
        </div>

        <div className="flex items-center justify-between mt-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            Connected
          </span>
          <div className="flex items-center gap-1.5">
            <button
              title="View details"
              onClick={e => { e.stopPropagation(); onClick(); }}
              className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              title="Transfer"
              onClick={e => e.stopPropagation()}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {canHangup && (
              <button
                title="End call"
                onClick={e => { e.stopPropagation(); onHangup(call.session_id); }}
                disabled={isHangingUp}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
              >
                {isHangingUp
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <PhoneOff className="w-3.5 h-3.5" />
                }
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Transcript preview */}
      <div
		  className="px-4 py-3 space-y-2 bg-gray-50 rounded-b-xl overflow-y-auto"
		  style={{ height: '130px', minHeight: '130px', maxHeight: '130px' }}
	  >
		  {lastLines.length === 0 ? (
			<p className="text-xs text-gray-400 text-center pt-6">Waiting for conversation…</p>
		  ) : (
			lastLines.map((line, i) => (
			  <TranscriptRow key={i} speaker={line.speaker} text={line.text} compact />
			))
		  )}
	  </div>
    </div>
  );
}

// ── Detail modal ──────────────────────────────────────────────────────────────
function CallDetailModal({ call, transcript, onClose, onHangup, isHangingUp, canHangup }) {
  const transcriptRef = useRef(null);

  // Auto-scroll transcript to bottom on new messages
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript.length]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{call.caller_number || 'Unknown'}</h3>
              <p className="text-sm text-gray-500">{call.agent_name} · {call.provider}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canHangup && (
              <button
                onClick={() => onHangup(call.session_id)}
                disabled={isHangingUp}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
              >
                {isHangingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneOff className="w-4 h-4" />}
                Hangup
              </button>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Call meta */}
        <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Duration</p>
            <CallTimer startedAt={call.started_at} className="text-gray-900" />
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Session</p>
            <p className="text-xs font-mono text-gray-600 truncate">{call.session_id?.slice(-12)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Started</p>
            <p className="text-xs text-gray-600">{new Date(call.started_at).toLocaleTimeString()}</p>
          </div>
        </div>

        {/* Full transcript */}
        <div ref={transcriptRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {transcript.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Waiting for conversation to start…</p>
            </div>
          ) : (
            transcript.map((line, i) => (
              <TranscriptRow key={i} speaker={line.speaker} text={line.text} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Monitor() {
  const { user: currentUser } = useAuth();
  const canHangup = currentUser?.role !== 'viewer';
  const [activeCalls, setActiveCalls]         = useState([]);
  const [stats, setStats]                     = useState(null);
  const [liveTranscripts, setLiveTranscripts] = useState({});
  const [loading, setLoading]                 = useState(true);
  const [hangingUp, setHangingUp]             = useState({});
  const [hangingUpAll, setHangingUpAll]       = useState(false);
  const [showConfirmAll, setShowConfirmAll]   = useState(false);
  const [selectedCall, setSelectedCall]       = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const socketRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [callsRes, statsRes] = await Promise.all([
        api.getActiveCalls(),
        api.getMonitorStats(),
      ]);
      setActiveCalls(callsRes.data.calls || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('[Monitor] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const token = localStorage.getItem('token');
    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1500,
    });
    socketRef.current = socket;

    socket.on('connect', () => { setSocketConnected(true); loadData(); });
    socket.on('disconnect', () => setSocketConnected(false));

    socket.on('transcript', ({ sessionId, speaker, text, timestamp }) => {
      setLiveTranscripts(prev => {
        const existing = prev[sessionId] || [];
        const last = existing[existing.length - 1];
        // Deduplicate consecutive identical entries
        if (last && last.speaker === speaker && last.text === text) return prev;
        return {
          ...prev,
          [sessionId]: [
            ...existing,
            { speaker: normalizeSpeaker(speaker), text, timestamp }
          ].slice(-50),
        };
      });
    });

    socket.on('call_event', ({ event: evt, sessionId }) => {
      if (evt === 'call_started') {
        loadData();
      } else if (evt === 'call_ended') {
        setActiveCalls(prev => prev.filter(c => c.session_id !== sessionId));
        setLiveTranscripts(prev => { const n = { ...prev }; delete n[sessionId]; return n; });
        setSelectedCall(prev => prev?.session_id === sessionId ? null : prev);
        loadData();
      }
    });

    // Safety-net poll every 15s
    const poll = setInterval(loadData, 15000);

    return () => { socket.disconnect(); clearInterval(poll); };
  }, [loadData]);

  const getTranscript = (call) => {
    const live = liveTranscripts[call.session_id];
    if (live && live.length > 0) return live;
    if (Array.isArray(call.transcript)) {
      return call.transcript.map(t => ({ ...t, speaker: normalizeSpeaker(t.speaker) }));
    }
    return [];
  };

  const handleHangup = async (sessionId) => {
    setHangingUp(prev => ({ ...prev, [sessionId]: true }));
    try {
      await api.hangupCall(sessionId);
      toast.success('Call ended');
      setActiveCalls(prev => prev.filter(c => c.session_id !== sessionId));
      setLiveTranscripts(prev => { const n = { ...prev }; delete n[sessionId]; return n; });
      if (selectedCall?.session_id === sessionId) setSelectedCall(null);
      setStats(prev => prev ? { ...prev, active_calls: Math.max(0, (prev.active_calls || 1) - 1) } : prev);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to end call');
    } finally {
      setHangingUp(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const handleHangupAll = async () => {
    setHangingUpAll(true);
    try {
      const res = await api.hangupAllCalls();
      toast.success(`Ended ${res.data.ended || 0} calls`);
      setActiveCalls([]); setLiveTranscripts({}); setSelectedCall(null);
      setShowConfirmAll(false); loadData();
    } catch {
      toast.error('Failed to end all calls');
    } finally {
      setHangingUpAll(false);
    }
  };

  const fmtDuration = (secs) => {
    if (!secs) return '0s';
    const m = Math.floor(secs / 60), s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Monitor</h1>
          <p className="text-gray-500 flex items-center gap-2 mt-0.5">
            <span className={`inline-flex items-center gap-1.5 text-sm ${socketConnected ? 'text-green-600' : 'text-gray-400'}`}>
              {socketConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              {socketConnected ? 'Real-time connected' : 'Reconnecting…'}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canHangup && activeCalls.length > 0 && (
            <button
              onClick={() => setShowConfirmAll(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-medium transition-colors"
            >
              <PhoneOff className="w-4 h-4" />
              Hangup All ({activeCalls.length})
            </button>
          )}
          <button onClick={loadData} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Active Calls',  value: stats.active_calls    || 0, icon: Phone,    color: 'text-green-600',   bg: 'bg-green-100' },
            { label: 'Today',         value: stats.calls_today     || 0, icon: Clock,    color: 'text-blue-600',    bg: 'bg-blue-100' },
            { label: 'Completed',     value: stats.completed_today || 0, icon: Phone,    color: 'text-primary-600', bg: 'bg-primary-100' },
            { label: 'Failed',        value: stats.failed_today    || 0, icon: PhoneOff, color: 'text-red-600',     bg: 'bg-red-100' },
            { label: 'Avg Duration',  value: fmtDuration(stats.avg_duration), icon: Clock, color: 'text-orange-600', bg: 'bg-orange-100' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${bg}`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className={`text-xl font-bold ${color}`}>{value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Call grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : activeCalls.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 py-16 text-center">
          <PhoneOff className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No active calls</h3>
          <p className="text-gray-500 text-sm">New calls will appear instantly when they connect</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {activeCalls.map(call => (
            <CallCard
              key={call.session_id}
              call={call}
              transcript={getTranscript(call)}
              onHangup={handleHangup}
              isHangingUp={!!hangingUp[call.session_id]}
              onClick={() => setSelectedCall(call)}
              canHangup={canHangup}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedCall && (
        <CallDetailModal
          call={selectedCall}
          transcript={getTranscript(selectedCall)}
          onClose={() => setSelectedCall(null)}
          onHangup={handleHangup}
          isHangingUp={!!hangingUp[selectedCall.session_id]}
          canHangup={canHangup}
        />
      )}

      {/* Hangup all confirmation */}
      {showConfirmAll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900">End All Active Calls?</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              This will immediately hang up all {activeCalls.length} active call{activeCalls.length !== 1 ? 's' : ''}. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirmAll(false)} className="flex-1 py-2 px-4 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleHangupAll}
                disabled={hangingUpAll}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {hangingUpAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneOff className="w-4 h-4" />}
                End All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}